// Planning domain rules shared by server and client (docs/03 R04–R07, docs/06 required copy).
import { z } from "zod";
import { LIMITS } from "./limits.ts";
import { isIsoDate } from "./dates.ts";
import { optionalText, requiredText, uuid, version } from "./setup.ts";

export type InitiativeStatus = "draft" | "in_review" | "changes_requested" | "validated";
export const STATUS_LABEL: Record<InitiativeStatus, string> = {
  draft: "Rascunho",
  in_review: "Em revisão",
  changes_requested: "Ajustes solicitados",
  validated: "Validada",
};
export const EDITABLE_STATUSES: InitiativeStatus[] = ["draft", "changes_requested"];

export const MSG = {
  weight: "Use um peso de 5 a 100, em múltiplos de 5.",
  dates: "O término não pode ser anterior ao início.",
  minActions: "Cadastre pelo menos duas ações para submeter esta iniciativa.",
  remaining: (n: number) => `Distribua os ${n} pontos restantes.`,
  excess: (n: number) => `Reduza ${n} pontos para totalizar 100.`,
  unit: "Indique a unidade responsável.",
  unitArchived: "A unidade indicada foi arquivada. Escolha uma unidade ativa.",
  actionDescription: "Descreva a ação.",
  actionStart: "Informe a data de início.",
  actionEnd: "Informe a data de término.",
  actionWeight: "Informe o peso.",
  collection: `Use no máximo ${LIMITS.collection} itens.`,
};

const nullableDate = z
  .string()
  .nullish()
  .transform((v) => (v == null || v === "" ? null : v))
  .refine((v) => v === null || isIsoDate(v), "Informe uma data válida.");

export const actionSchema = z
  .object({
    id: uuid.nullish(),
    description: optionalText(LIMITS.actionDescription),
    starts_on: nullableDate,
    ends_on: nullableDate,
    weight: z
      .union([z.number(), z.null(), z.literal("")])
      .optional()
      .transform((v) => (v === "" || v === undefined ? null : v))
      .refine((v) => v === null || (Number.isInteger(v) && v >= 5 && v <= 100 && v % 5 === 0), MSG.weight),
    sort_order: z.number().int().optional(),
  })
  .refine((a) => !a.starts_on || !a.ends_on || a.ends_on >= a.starts_on, { path: ["ends_on"], message: MSG.dates });

export type ActionInput = z.input<typeof actionSchema>;
export type ActionValue = z.output<typeof actionSchema>;

const initiativeFields = {
  title: requiredText(LIMITS.title, "Informe o título da iniciativa."),
  description: optionalText(LIMITS.longDescription),
  unit_id: uuid.nullish().transform((v) => v ?? null),
  actions: z.array(actionSchema).max(LIMITS.collection, MSG.collection).default([]),
};

export const initiativeCreateSchema = z.object({ key_result_id: uuid, ...initiativeFields });
export const initiativeSaveSchema = z.object({ expected_version: version, ...initiativeFields });

export const objectiveCreateSchema = z.object({
  axis_id: uuid,
  title: requiredText(LIMITS.title, "Informe o objetivo."),
  description: optionalText(LIMITS.longDescription),
  sort_order: z.number().int().optional(),
});
export const keyResultCreateSchema = z.object({
  objective_id: uuid,
  title: requiredText(LIMITS.title, "Informe o KR."),
  description: optionalText(LIMITS.longDescription),
  sort_order: z.number().int().optional(),
});
export const hierarchyUpdateSchema = z.object({
  expected_version: version,
  title: requiredText(LIMITS.title, "Informe o título.").optional(),
  description: optionalText(LIMITS.longDescription).optional(),
  sort_order: z.number().int().optional(),
});

export interface PendingField {
  path: string;
  message: string;
}

export interface Readiness {
  weight_total: number;
  complete_actions: number;
  pending_fields: PendingField[];
  ready: boolean;
}

export const isCompleteAction = (a: Pick<ActionValue, "description" | "starts_on" | "ends_on" | "weight">) =>
  Boolean(a.description && a.starts_on && a.ends_on && a.weight !== null && a.ends_on >= a.starts_on);

/**
 * Submission readiness (R05/R06). Drafts may be saved with any of these pending; submission requires none.
 * `unitActive` is false when the unit exists but was archived.
 */
export function submissionReadiness(input: {
  title: string | null;
  unit_id: string | null;
  unitActive: boolean;
  actions: Pick<ActionValue, "description" | "starts_on" | "ends_on" | "weight">[];
}): Readiness {
  const pending: PendingField[] = [];
  if (!input.title?.trim()) pending.push({ path: "title", message: "Informe o título da iniciativa." });
  if (!input.unit_id) pending.push({ path: "unit_id", message: MSG.unit });
  else if (!input.unitActive) pending.push({ path: "unit_id", message: MSG.unitArchived });
  input.actions.forEach((a, i) => {
    if (!a.description) pending.push({ path: `actions.${i}.description`, message: MSG.actionDescription });
    if (!a.starts_on) pending.push({ path: `actions.${i}.starts_on`, message: MSG.actionStart });
    if (!a.ends_on) pending.push({ path: `actions.${i}.ends_on`, message: MSG.actionEnd });
    if (a.weight === null) pending.push({ path: `actions.${i}.weight`, message: MSG.actionWeight });
  });
  const complete = input.actions.filter(isCompleteAction).length;
  if (input.actions.length < 2) pending.push({ path: "actions", message: MSG.minActions });
  const total = input.actions.reduce((s, a) => s + (a.weight ?? 0), 0);
  if (total < 100) pending.push({ path: "weight_total", message: MSG.remaining(100 - total) });
  else if (total > 100) pending.push({ path: "weight_total", message: MSG.excess(total - 100) });
  return { weight_total: total, complete_actions: complete, pending_fields: pending, ready: pending.length === 0 };
}

/** Initiative dates derive from its actions; unknown when any needed date is missing (R06). */
export function derivedDates(actions: Pick<ActionValue, "starts_on" | "ends_on">[]) {
  if (actions.length === 0 || actions.some((a) => !a.starts_on || !a.ends_on)) return { starts_on: null, ends_on: null };
  const starts = actions.map((a) => a.starts_on!).sort();
  const ends = actions.map((a) => a.ends_on!).sort();
  return { starts_on: starts[0]!, ends_on: ends[ends.length - 1]! };
}

export const RECOMMENDED = { min: 2, max: 5 };
/** Advisory only (R07): never blocks drafts or submission. */
export function guidance(count: number, noun: "KRs" | "iniciativas"): string | null {
  if (count < RECOMMENDED.min) return `Recomendação metodológica: de 2 a 5 ${noun}.`;
  if (count > RECOMMENDED.max) return `Acima da recomendação de até 5 ${noun}. Isso não impede o envio.`;
  return null;
}
